import React from 'react';

import { ButtonBase, Typography } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import { Link } from 'react-router-dom';
import { useUserLogContext } from 'context/UserLogContext/UserLogContext';

const useStyles = makeStyles(theme => ({
  button: {
    display: 'block',
  },
  card: {
    position: 'relative',
    padding: ({ isFeature }) => (isFeature ? theme.spacing(6) : theme.spacing(8)),
    height: '100%',
    width: '100%',
    boxShadow: theme.shadows[3],
    cursor: ({ clickable }) => (clickable ? 'pointer' : 'auto'),

    transition: theme.transitions.create(['box-shadow'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.standard,
    }),

    '&:hover': {
      boxShadow: theme.shadows[5],
    },

    '@media(max-width: 740px)': {
      padding: ({ denseMobile, isFeature }) =>
        isFeature
          ? denseMobile
            ? theme.spacing(3.5, 2.5)
            : theme.spacing(5, 3.5)
          : denseMobile
          ? theme.spacing(4.5, 3.5)
          : theme.spacing(7),
    },
    [theme.breakpoints.down('xs')]: {
      padding: ({ denseMobile, isFeature }) =>
        isFeature ? (denseMobile ? theme.spacing(3, 2.5) : theme.spacing(4.5, 3)) : denseMobile ? theme.spacing(4, 3) : theme.spacing(7, 5),
    },
    '& h2': {
      padding: ({ isFeature }) => (isFeature ? 0 : theme.spacing(6, 0)),
      color: ({ disabled }) => (disabled ? theme.palette.text.disabled : theme.palette.primary.main),
      fontSize: ({ isFeature }) => (isFeature ? '1.35rem' : '1.75rem'),
      fontWeight: 700,
      textAlign: ({ isFeature }) => (isFeature ? 'left' : 'center'),
      lineHeight: 1.25,
      '@media(max-width: 740px)': {
        fontSize: ({ denseMobile, isFeature }) => {
          if (isFeature) return denseMobile ? '1.05rem' : '1.15rem';
          return denseMobile ? '1.15rem' : '1.35rem';
        },
        padding: ({ denseMobile, isFeature }) => (isFeature ? 0 : denseMobile ? theme.spacing(2.5, 0) : theme.spacing(5, 0)),
      },
    },
  },
  featureContent: {
    display: 'grid',
    gridTemplateColumns: '36px 1fr',
    columnGap: theme.spacing(3),
    alignItems: 'start',
    minHeight: '100%',
    '@media(max-width: 740px)': {
      gridTemplateColumns: '24px 1fr',
      columnGap: ({ denseMobile }) => (denseMobile ? theme.spacing(2) : theme.spacing(2.5)),
    },
  },
  featureText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    textAlign: 'left',
    minWidth: 0,
  },
  featureIcon: {
    width: '36px',
    minWidth: '36px',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    '@media(max-width: 740px)': {
      width: '24px',
      minWidth: '24px',
    },
    '& svg': {
      width: '100%',
      height: 'auto',
      display: 'block',
    },
  },
  description: {
    marginTop: theme.spacing(1.5),
    color: ({ disabled }) => (disabled ? theme.palette.text.disabled : theme.palette.text.secondary),
    fontSize: '0.875rem',
    lineHeight: 1.45,
    textAlign: 'left',
    '@media(max-width: 740px)': {
      marginTop: ({ denseMobile }) => (denseMobile ? theme.spacing(1) : theme.spacing(1.5)),
      fontSize: ({ denseMobile }) => (denseMobile ? '0.75rem' : '0.8125rem'),
      lineHeight: 1.4,
    },
  },
  additionalText: {
    position: 'absolute',
    width: '100%',
    bottom: theme.spacing(4),
    left: 0,
    textAlign: 'center',
    fontSize: '.8rem',
    color: theme.palette.text.disabled,
    '@media(max-width: 740px)': {
      bottom: ({ denseMobile }) => (denseMobile ? theme.spacing(2.5) : theme.spacing(4)),
      fontSize: ({ denseMobile }) => (denseMobile ? '.72rem' : '.8rem'),
    },
  },
  additionalTextBadge: {
    position: 'absolute',
    top: -theme.spacing(2),
    right: theme.spacing(1),
    fontSize: '0.8125rem',
    fontWeight: 500,
    lineHeight: 1.2,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    borderRadius: theme.spacing(20),
    padding: theme.spacing(1, 2.5),
    whiteSpace: 'nowrap',
    boxShadow: theme.shadows[4],
    '@media(max-width: 740px)': {
      top: ({ denseMobile }) => (denseMobile ? -theme.spacing(1.5) : -theme.spacing(2)),
      right: ({ denseMobile }) => (denseMobile ? -theme.spacing(1.5) : -theme.spacing(2)),
      fontSize: ({ denseMobile }) => (denseMobile ? '0.75rem' : '0.8125rem'),
      borderRadius: theme.spacing(20),
      padding: ({ denseMobile }) => (denseMobile ? theme.spacing(0.75, 2.25) : theme.spacing(1, 2.5)),
    },
  },
  title: {
    position: 'absolute',
    top: theme.spacing(6),
    left: theme.spacing(6),
    textAlign: 'center',
    fontSize: '.875rem',
    fontWeight: 700,
    color: theme.palette.text.disabled,
    '@media(max-width: 740px)': {
      top: ({ denseMobile }) => (denseMobile ? theme.spacing(3) : theme.spacing(4)),
      left: ({ denseMobile }) => (denseMobile ? theme.spacing(3) : theme.spacing(4)),
    },
  },
  link: {
    height: '100%',
    width: '100%',
  },
}));

const DashboardCard = ({
  children,
  additionalText,
  title,
  heading,
  description,
  to,
  className,
  disabled,
  ariaLabel,
  clickable,
  denseMobile,
  layout,
}) => {
  const { logEvent } = useUserLogContext();
  const onClick = () => {
    logEvent('dashboard-click', { button: ariaLabel });
  };
  const isFeature = layout === 'feature';
  const hasAdditionalText = Boolean(additionalText);
  const classes = useStyles({ disabled, clickable, denseMobile, isFeature });
  const cardContents = (
    <Paper className={classes.card} square>
      <Typography className={classes.title}>{title}</Typography>
      {isFeature ? (
        <div className={classes.featureContent}>
          <div className={classes.featureIcon}>{children}</div>
          <div className={classes.featureText}>
            {heading ? <Typography variant='h2'>{heading}</Typography> : null}
            {description ? (
              <Typography className={classes.description} component='p' variant='body2'>
                {description}
              </Typography>
            ) : null}
          </div>
        </div>
      ) : (
        children
      )}
      {hasAdditionalText ? (
        <Typography className={isFeature ? classes.additionalTextBadge : classes.additionalText} component={isFeature ? 'span' : 'p'}>
          {additionalText}
        </Typography>
      ) : null}
    </Paper>
  );
  const rootClassName = clsx(classes.button, classes.link, className);
  const hasDestination = Boolean(to);

  if (disabled || !hasDestination) {
    return (
      <ButtonBase aria-label={ariaLabel} className={rootClassName} component='div' disabled={disabled} disableRipple tabIndex={-1}>
        {cardContents}
      </ButtonBase>
    );
  }

  return (
    <ButtonBase aria-label={ariaLabel} className={rootClassName} component={Link} disableRipple={!clickable} onClick={onClick} to={to}>
      {cardContents}
    </ButtonBase>
  );
};

DashboardCard.defaultProps = {
  to: null,
  className: '',
  disabled: false,
  additionalText: '',
  title: '',
  heading: '',
  description: '',
  clickable: true,
  denseMobile: false,
  layout: 'default',
};

export default DashboardCard;
